/**
 * @file components/staff-profile/ConflictTab.tsx
 * @description 利益迴避分頁（只讀）。
 *
 * 功能：
 *  - 展示員工的利益迴避記錄（客戶名稱、迴避原因、建立時間）
 *  - 提示說明：利益迴避統一由「設定 → 利益迴避」管理，並提供跳轉連結
 *
 * 資料來自父層 profile 查詢的 conflicts 片段（只讀，無增刪操作）。
 */

import { Alert, Table } from 'antd';
import { Link } from '@tanstack/react-router';
import { ShieldAlert } from 'lucide-react';
import { SectionCard } from '@/components/shell';
import type { ColumnsType } from 'antd/es/table';

// ─── 型別 ─────────────────────────────────────────────────────────────────────

/** 利益迴避記錄（後端回傳，對應 conflict_of_interest 表） */
export interface ConflictRecord {
  id: number;
  /** 稽核員 ID */
  auditorId: number;
  /** 涉及的客戶 ID */
  clientId: number;
  /** 迴避原因說明 */
  reason: string;
  /** 建立時間（ISO 字串） */
  createdAt: string;
}

// ─── 組件 Props ───────────────────────────────────────────────────────────────

export interface ConflictTabProps {
  /** 員工 userId（保留供未來擴充用，目前只讀） */
  userId: number;
  /** 利益迴避記錄列表（來自 profile 查詢） */
  conflicts: ConflictRecord[];
}

// ─── 主組件 ──────────────────────────────────────────────────────────────────

/**
 * 利益迴避 Tab（只讀）。
 * 記錄展示後附提示說明，提醒管理員到「設定 → 利益迴避」統一維護。
 */
export function ConflictTab({ conflicts }: ConflictTabProps) {
  const columns: ColumnsType<ConflictRecord> = [
    {
      title: '客戶 ID',
      dataIndex: 'clientId',
      width: 90,
      render: (v: number) => `#${v}`,
    },
    {
      title: '迴避原因',
      dataIndex: 'reason',
      ellipsis: true,
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('zh-TW'),
    },
  ];

  return (
    <SectionCard
      title="利益迴避記錄"
      icon={<ShieldAlert size={14} />}
      description="此員工與下列客戶存在利益迴避關係，稽核任務派案時系統將自動排除。"
      padless
      footer={
        <Alert
          type="info"
          showIcon
          message={
            <span>
              利益迴避聲明請至{' '}
              <Link to="/settings/conflict-of-interest" style={{ fontWeight: 600 }}>
                設定 → 利益迴避
              </Link>{' '}
              統一管理（新增 / 修改 / 解除）。
            </span>
          }
          style={{ margin: '0', borderRadius: 0, border: 'none' }}
        />
      }
    >
      <Table<ConflictRecord>
        rowKey="id"
        size="small"
        dataSource={conflicts}
        columns={columns}
        pagination={false}
        locale={{ emptyText: '目前無利益迴避記錄' }}
      />
    </SectionCard>
  );
}
